// MARK://dhk8/admin-shell/components/FilterBar
// Barra de filtros PRESENTACIONAL para listados del admin RBAC (Gustavo 2026-06-22).
// Búsqueda con debounce (~300ms) + selects de filtro + slot `actions` a la derecha.
// Una fila responsive. El padre maneja el fetch: aquí solo se disparan callbacks.
// - El input es "uncontrolled-ish": mantiene su texto local y emite onSearch
//   debounced; si el padre cambia `search` externamente, se resincroniza.
// - Estilos propios en <style> con clases `nui-*` (NO toca styles.css).
//   Reusa el look de los controles del shell (mismas alturas/curvas/tokens).
import { useEffect, useRef, useState, type ReactNode } from 'react'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterDef {
  key: string
  label: string
  options: FilterOption[]
  value: string
  onChange: (value: string) => void
}

export interface FilterBarProps {
  search?: string
  onSearch?: (q: string) => void
  searchPlaceholder?: string
  filters?: FilterDef[]
  /** Slot a la derecha (p.ej. botón "Nuevo"). */
  actions?: ReactNode
  /** Debounce de la búsqueda en ms (default 300). */
  debounceMs?: number
}

export default function FilterBar({
  search,
  onSearch,
  searchPlaceholder = 'Buscar…',
  filters,
  actions,
  debounceMs = 300,
}: FilterBarProps) {
  const [local, setLocal] = useState(search ?? '')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Guarda la última cadena emitida para no re-emitir cuando el padre
  // refleja de vuelta el mismo `search` que acabamos de enviar.
  const lastEmitted = useRef(search ?? '')

  // Resync si el padre cambia `search` por su cuenta (reset de filtros, etc.).
  useEffect(() => {
    if (search !== undefined && search !== lastEmitted.current) {
      setLocal(search)
      lastEmitted.current = search
    }
  }, [search])

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const handleType = (v: string) => {
    setLocal(v)
    if (!onSearch) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      lastEmitted.current = v
      onSearch(v)
    }, debounceMs)
  }

  const flush = () => {
    if (!onSearch) return
    if (timer.current) clearTimeout(timer.current)
    lastEmitted.current = local
    onSearch(local)
  }

  const showSearch = typeof onSearch === 'function' || search !== undefined

  return (
    <div className="nui-filterbar">
      <style>{NUI_FILTERBAR_CSS}</style>

      {showSearch && (
        <div className="nui-filterbar__search">
          <span className="nui-filterbar__search-icon" aria-hidden="true">⌕</span>
          <input
            type="search"
            className="nui-filterbar__input"
            value={local}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onChange={(e) => handleType(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') flush()
            }}
          />
        </div>
      )}

      {filters?.map((f) => (
        <label key={f.key} className="nui-filterbar__filter">
          <span className="nui-filterbar__filter-label">{f.label}</span>
          <span className="nui-filterbar__select-wrap">
            <select
              className="nui-filterbar__select"
              value={f.value}
              onChange={(e) => f.onChange(e.target.value)}
              aria-label={f.label}
            >
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="nui-filterbar__chevron" aria-hidden="true" />
          </span>
        </label>
      ))}

      {actions != null && <div className="nui-filterbar__actions">{actions}</div>}
    </div>
  )
}

const NUI_FILTERBAR_CSS = `
.nui-filterbar {
  display: flex; align-items: flex-end; gap: var(--pos-space-2);
  flex-wrap: wrap;
}
.nui-filterbar__search {
  position: relative; display: flex; align-items: center;
  flex: 1 1 220px; min-width: 180px;
}
.nui-filterbar__search-icon {
  position: absolute; left: var(--pos-space-3);
  color: var(--pos-fg-tertiary); font-size: var(--pos-text-md);
  pointer-events: none; line-height: 1;
}
.nui-filterbar__input {
  width: 100%; min-height: 44px;
  padding: 0 var(--pos-space-3) 0 calc(var(--pos-space-3) + 20px);
  border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md);
  background: var(--pos-canvas); color: var(--pos-fg);
  font-family: var(--font-sans); font-size: var(--pos-text-sm);
}
.nui-filterbar__input:focus {
  outline: none; border-color: var(--pos-tint);
  box-shadow: 0 0 0 3px var(--pos-tint-soft);
}
.nui-filterbar__input::-webkit-search-cancel-button { cursor: pointer; }
.nui-filterbar__filter { display: flex; flex-direction: column; gap: 6px; min-width: 150px; }
.nui-filterbar__filter-label {
  font-size: var(--pos-text-xxs); font-weight: var(--pos-weight-medium);
  color: var(--pos-fg-tertiary);
  text-transform: uppercase; letter-spacing: var(--pos-tracking-wide, 0.04em);
  padding-left: 2px;
}
.nui-filterbar__select-wrap { position: relative; display: block; }
.nui-filterbar__select {
  appearance: none; -webkit-appearance: none;
  width: 100%; min-height: 44px;
  padding: 0 calc(var(--pos-space-3) + 18px) 0 var(--pos-space-3);
  border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md);
  background: var(--pos-canvas); color: var(--pos-fg);
  font-family: var(--font-sans); font-size: var(--pos-text-sm);
  cursor: pointer;
}
.nui-filterbar__select:hover { border-color: var(--pos-separator-strong); }
.nui-filterbar__select:focus {
  outline: none; border-color: var(--pos-tint);
  box-shadow: 0 0 0 3px var(--pos-tint-soft);
}
/* Chevron propio (theme-aware, sin hardcode) — cuadrado rotado usando bordes. */
.nui-filterbar__chevron {
  position: absolute; right: calc(var(--pos-space-3) - 1px); top: 50%;
  width: 8px; height: 8px; pointer-events: none;
  border-right: 1.5px solid var(--pos-fg-tertiary);
  border-bottom: 1.5px solid var(--pos-fg-tertiary);
  transform: translateY(-65%) rotate(45deg);
}
.nui-filterbar__actions {
  display: flex; align-items: center; gap: var(--pos-space-2);
  margin-left: auto; flex-wrap: wrap;
}
`
